import React, { useState } from 'react';

import Dialog from '@material-ui/core/Dialog';

import CloseIcon from '@material-ui/icons/Close';
import MailOutlinedIcon from '@material-ui/icons/MailOutlined';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@material-ui/icons/VisibilityOffOutlined';
import CardGiftcardOutlinedIcon from '@material-ui/icons/CardGiftcardOutlined';

import * as S from '../RegisterModal/styles';
import { getVoucherAuth } from '../../services/firebase';
import { useGlobal } from '../../contexts/globalContext';
import UserRepository from '../../repositories/userRepository';
import Loader from '../Loader';

import { validEmail } from '../../utils/validator';
import { getCampaignColors } from '../../utils/getCampaignColors';

function AddVoucherModal({ open, onClose }) {
	const { user, setUser, showAlert } = useGlobal();

	const [email, setEmail] = useState('');
	const [pass, setPass] = useState('');
	const [showPass, setShowPass] = useState(false);
	const [error, setError] = useState('');
	const [openLoader, setOpenLoader] = useState(false);

	const accentColor = getCampaignColors()?.searchColor;
	const userRepository = new UserRepository();

	const reset = () => {
		setEmail('');
		setPass('');
		setError('');
		setShowPass(false);
	};

	const handleClose = () => {
		reset();
		onClose(false);
	};

	// Autentica o Vale Viagem em uma app Firebase secundária para NÃO trocar a sessão
	// do usuário autenticado (RN020 / CA016). Após capturar o UID, faz signOut da secundária.
	const authVoucherAndGetUid = async () => {
		const voucherAuth = getVoucherAuth();
		try {
			const credential = await voucherAuth.signInWithEmailAndPassword(email, pass);
			const uid = credential?.user?.uid;
			return uid || null;
		} finally {
			try {
				await voucherAuth.signOut();
			} catch (_) {
				// ignore
			}
		}
	};

	const handleSubmit = async () => {
		setError('');

		if (!email || !pass) {
			setError('Informe e-mail e senha do Vale Viagem.');
			return;
		}
		if (!validEmail(email)) {
			setError('Por favor, digite um e-mail válido.');
			return;
		}

		setOpenLoader(true);
		try {
			let voucherUid;
			try {
				voucherUid = await authVoucherAndGetUid();
			} catch (e) {
				// SEG003 / CA019: credenciais inválidas — não diferenciamos "usuário inexistente"
				// de "senha errada" por segurança.
				setOpenLoader(false);
				setError('Não foi possível validar os dados informados. Verifique as informações ou entre em contato com o atendimento.');
				return;
			}

			if (!voucherUid) {
				setOpenLoader(false);
				setError('Não foi possível validar os dados informados.');
				return;
			}

			const response = await userRepository
				.transferVoucher(voucherUid)
				.catch((err) => ({ status: err?.response?.status, data: err?.response?.data }));

			setOpenLoader(false);

			const status = response?.status;
			const data = response?.data || {};

			if (status === 200 && data?.user) {
				// RN018 / CA015: refletir saldo atualizado imediatamente
				setUser(data.user);
				sessionStorage.setItem('@App:user', JSON.stringify(data.user));
				showAlert('success', data.message || 'Seus pontos foram transferidos com sucesso para sua conta.');
				handleClose();
				return;
			}

			// Mensagens vindas do backend cobrem CA010/CA019 (Mensagens do Sistema)
			setError(data?.message || 'Não foi possível concluir a transferência. Tente novamente em instantes.');
		} catch (e) {
			setOpenLoader(false);
			setError('Não foi possível concluir a transferência. Tente novamente em instantes.');
		}
	};

	const handleEnter = (e) => {
		if (e.key === 'Enter') handleSubmit();
	};

	const passwordToggle = (
		<button
			type='button'
			className='toggle'
			aria-label={showPass ? 'Ocultar senha' : 'Mostrar senha'}
			onClick={() => setShowPass((s) => !s)}
		>
			{showPass ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
		</button>
	);

	const campaignName = user?.campaign?.name || user?.campaign?.campaignName;

	return (
		<S.Modal>
			<Loader openLoader={openLoader} isBooking={false} isRegister={false} isLogin={true} />
			<Dialog
				open={open}
				maxWidth={false}
				scroll='body'
				PaperProps={{ style: { width: '100%', maxWidth: 460, borderRadius: 12 } }}
				aria-labelledby='add-voucher-dialog-title'
			>
				<S.Banner isVoucher={true} accent={accentColor}>
					<CardGiftcardOutlinedIcon />
					<span className='banner-text'>Adicione um Vale Viagem à sua conta.</span>
					<button type='button' className='banner-close' aria-label='Fechar' onClick={handleClose}>
						<CloseIcon />
					</button>
				</S.Banner>

				<S.FormSection>
					<S.FormHeading center>Adicionar Vale Viagem</S.FormHeading>
					<S.FormSubtitle center>
						Informe o e-mail e a senha do Vale Viagem para transferir os pontos para sua conta.
					</S.FormSubtitle>

					{campaignName && (
						<S.PointsInfo accent={accentColor}>
							<span>
								Os pontos serão creditados na campanha <b>{campaignName}</b>, vinculada à sua conta atual.
							</span>
						</S.PointsInfo>
					)}

					<S.Field>
						<S.FieldLabel>E-mail do Vale Viagem</S.FieldLabel>
						<S.InputBox accent={accentColor}>
							<MailOutlinedIcon />
							<input
								type='email'
								placeholder='nome@exemplo.com'
								autoFocus
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								onKeyPress={handleEnter}
							/>
						</S.InputBox>
					</S.Field>

					<S.Field>
						<S.FieldLabel>Senha</S.FieldLabel>
						<S.InputBox accent={accentColor}>
							<LockOutlinedIcon />
							<input
								type={showPass ? 'text' : 'password'}
								placeholder='••••••••'
								value={pass}
								onChange={(e) => setPass(e.target.value)}
								onKeyPress={handleEnter}
							/>
							{passwordToggle}
						</S.InputBox>
					</S.Field>

					{error && <S.ErrorText>{error}</S.ErrorText>}

					<S.PrimaryButton
						type='button'
						accent={accentColor}
						onClick={handleSubmit}
						style={{ marginTop: 18 }}
					>
						Vincular Vale Viagem
					</S.PrimaryButton>

					<S.SecondaryButton
						type='button'
						accent={accentColor}
						onClick={handleClose}
						style={{ marginTop: 10 }}
					>
						Cancelar
					</S.SecondaryButton>
				</S.FormSection>
			</Dialog>
		</S.Modal>
	);
}

export default AddVoucherModal;
